import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '14 May 2025';

export function Privacy() {
  return (
    <div className="min-h-screen bg-bg-base text-white">
      <nav className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center font-bold text-white text-sm">Cr</div>
          <span className="font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></span>
        </Link>
        <Link to="/login" className="text-[13px] text-white/50 hover:text-white transition-colors">Log in</Link>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <h1 className="text-3xl font-bold mb-2">Privacy Policy</h1>
        <p className="text-white/40 text-sm mb-10">Effective date: {EFFECTIVE_DATE}</p>

        <div className="space-y-8 text-white/70 leading-relaxed text-[13.5px]">

          <section>
            <h2 className="text-base font-semibold text-white mb-2">1. Who We Are</h2>
            <p>
              Crosova ("we", "us", "our") is the data controller for information collected through{' '}
              <a href="https://crosova.com" className="text-primary-300 hover:underline">crosova.com</a> and our API.
              Contact us at:{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">2. What Data We Collect</h2>
            <div className="space-y-3">
              <div>
                <div className="text-white/80 font-medium text-[13px] mb-1">Account data</div>
                <p>Name, email address, and password (stored as a secure hash — we never store your plain text password).</p>
              </div>
              <div>
                <div className="text-white/80 font-medium text-[13px] mb-1">Listing content</div>
                <p>Titles, descriptions, photos, prices, and other product details you enter or upload.</p>
              </div>
              <div>
                <div className="text-white/80 font-medium text-[13px] mb-1">Marketplace credentials</div>
                <p>
                  OAuth access tokens for connected platforms (eBay, Etsy, Shopify). We never store your marketplace
                  username or password. For browser-automated platforms (Vinted, Depop), session data is stored
                  encrypted.
                </p>
              </div>
              <div>
                <div className="text-white/80 font-medium text-[13px] mb-1">Usage and technical data</div>
                <p>
                  IP address, browser/device type, pages visited, and actions taken within the platform. This is used
                  for security, debugging, and improving the service.
                </p>
              </div>
              <div>
                <div className="text-white/80 font-medium text-[13px] mb-1">Billing data</div>
                <p>
                  Payment is handled by Stripe. We store your Stripe customer ID and subscription status but never
                  receive or store your full card details.
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">3. How We Use Your Data</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li>To provide, operate, and improve the Crosova service</li>
              <li>To process your subscription and send billing-related emails</li>
              <li>To cross-list your items to marketplaces on your instruction</li>
              <li>To generate AI-powered listing content on your request (via Google Gemini)</li>
              <li>To detect and prevent fraud, abuse, and security incidents</li>
              <li>To comply with legal obligations</li>
            </ul>
            <p className="mt-3">
              We do not sell your personal data or use it for advertising purposes.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">4. Legal Basis for Processing (UK GDPR)</h2>
            <ul className="list-disc pl-5 space-y-1">
              <li><span className="text-white/80">Contract</span> — processing necessary to provide the service you signed up for</li>
              <li><span className="text-white/80">Legitimate interests</span> — security monitoring, service improvement, fraud prevention</li>
              <li><span className="text-white/80">Legal obligation</span> — where required by law</li>
              <li><span className="text-white/80">Consent</span> — where you have explicitly opted in (e.g. marketing emails)</li>
            </ul>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">5. Third-Party Services</h2>
            <p>We use the following third-party processors:</p>
            <div className="mt-3 space-y-2">
              {[
                { name: 'Stripe', purpose: 'Payment processing', location: 'USA (Privacy Shield/SCCs)' },
                { name: 'Cloudflare R2', purpose: 'Photo and file storage', location: 'EU (Ireland)' },
                { name: 'Supabase (PostgreSQL)', purpose: 'Database hosting', location: 'EU (Ireland)' },
                { name: 'Railway', purpose: 'API server hosting', location: 'EU (Ireland)' },
                { name: 'Google Gemini', purpose: 'AI content generation', location: 'USA (SCCs)' },
                { name: 'Replicate', purpose: 'Photo background removal', location: 'USA (SCCs)' },
                { name: 'Upstash', purpose: 'Job queue (Redis)', location: 'EU' },
              ].map(({ name, purpose, location }) => (
                <div key={name} className="flex gap-4 text-[12.5px] border-b border-white/[0.04] pb-2">
                  <div className="w-36 text-white/80 font-medium flex-shrink-0">{name}</div>
                  <div className="flex-1">{purpose}</div>
                  <div className="text-white/40 text-right">{location}</div>
                </div>
              ))}
            </div>
            <p className="mt-3 text-[12px] text-white/50">
              SCCs = Standard Contractual Clauses (EU-approved transfer mechanism).
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">6. Data Retention</h2>
            <p>
              We keep your account data for as long as your account is active, plus 30 days after deletion to allow
              for recovery. After that, all personal data is permanently deleted. Billing records are retained for 7
              years as required by UK tax law.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">7. Your Rights (UK GDPR)</h2>
            <p>You have the following rights regarding your personal data:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li><span className="text-white/80">Access</span> — request a copy of the data we hold about you</li>
              <li><span className="text-white/80">Rectification</span> — ask us to correct inaccurate data</li>
              <li><span className="text-white/80">Erasure</span> — ask us to delete your data ("right to be forgotten")</li>
              <li><span className="text-white/80">Restriction</span> — ask us to limit how we use your data</li>
              <li><span className="text-white/80">Portability</span> — receive your data in a machine-readable format</li>
              <li><span className="text-white/80">Objection</span> — object to processing based on legitimate interests</li>
            </ul>
            <p className="mt-3">
              To exercise any of these rights, email us at{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>.
              We will respond within 30 days. You also have the right to lodge a complaint with the{' '}
              <a
                href="https://ico.org.uk"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary-300 hover:underline"
              >
                ICO (ico.org.uk)
              </a>
              .
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">8. Cookies</h2>
            <p>
              We use a single session cookie (<code className="bg-white/[0.07] px-1.5 py-0.5 rounded text-[12px]">crosova_token</code>) to keep you logged in.
              This cookie is strictly necessary for the service to function and does not track you for advertising
              purposes. No third-party tracking cookies are used.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">9. Security</h2>
            <p>
              We use industry-standard measures to protect your data: TLS encryption in transit, encrypted storage,
              hashed passwords (argon2), and rate limiting to prevent brute-force attacks. However, no system is
              completely secure, and we cannot guarantee absolute security.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">10. Children</h2>
            <p>
              Crosova is not intended for users under 18 years of age. We do not knowingly collect data from anyone
              under 18. If you believe a child has provided us with personal data, please contact us.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">11. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. We will notify you by email at least 14 days before
              material changes take effect. The effective date at the top of this page will always reflect the current
              version.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">12. Contact</h2>
            <p>
              Data protection queries:{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16 py-8 text-center text-[11.5px] text-white/30">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link to="/terms" className="hover:text-white/60 transition-colors">Terms</Link>
          <Link to="/privacy" className="text-white/50">Privacy</Link>
          <a href="mailto:admin@crosova.com" className="hover:text-white/60 transition-colors">Contact</a>
        </div>
        <div>© {new Date().getFullYear()} Crosova. All rights reserved.</div>
      </footer>
    </div>
  );
}
